<template>
  <a-modal
    :open="modalOpen"
    :width="modalWidth"
    :mask="mask"
    :mask-closable="maskClosable"
    :closable="false"
    :destroy-on-close="destroyOnClose"
    :wrap-class-name="wrapClassName"
    :z-index="zIndex"
    :get-container="getContainer"
    @cancel="handleCancel"
  >
    <!-- 统一自定义头部 -->
    <template #title>
      <div :class="headerClass">
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <img v-if="headerImg" :src="headerImg" class="h-12 w-12 flex-shrink-0 rounded-lg object-cover" />
          <div v-if="headerTitle || headerSubtitle" class="min-w-0">
            <div :class="headerTitleClass">{{ headerTitle }}</div>
            <div v-if="headerSubtitle" :class="headerSubtitleClass">{{ headerSubtitle }}</div>
          </div>
        </div>
        <div v-if="showHeaderClose" :class="headerCloseClass" @click="handleCancel">
          <slot name="headerCloseIcon">
            <span>✕</span>
          </slot>
        </div>
      </div>
    </template>

    <div class="flex flex-col overflow-hidden" :class="(isMobile && fullscreen) || alwaysFullscreen ? 'h-full' : 'max-h-[60dvh]'">
      <div
        class="flex-1"
        :class="[bodyScrollable ? 'overflow-y-auto' : 'overflow-hidden', bodyClass]"
      >
        <slot></slot>
      </div>
    </div>
    <!-- 统一自定义底部 -->
    <template #footer>
      <slot name="customFooter">
        <div v-if="showResetButton || showConfirmButton" :class="footerClass">
          <div
            v-if="showResetButton"
            :class="resetButtonClass"
            @click="handleReset"
          >
            {{ resetButtonText }}
          </div>
          <div
            v-if="showConfirmButton"
            :class="confirmButtonClass"
            @click="handleConfirm"
          >
            {{ confirmButtonText }}
          </div>
        </div>
      </slot>
    </template>
  </a-modal>
</template>

<script>
export default {
  name: 'CustomModal',
  inheritAttrs: false,
  props: {
    // 是否显示（推荐使用 open，兼容旧版 visible）
    open: {
      type: Boolean,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    },
    zIndex: {
      type: Number,
      default: 100001
    },
    /** 挂载节点，嵌套在其它 Modal 内时建议 document.body */
    getContainer: {
      type: Function,
      default: () => document.body
    },
    // 宽度
    width: {
      type: [String, Number],
      default: 600
    },
    // 点击遮罩是否关闭
    maskClosable: {
      type: Boolean,
      default: true
    },
    // 是否显示遮罩层（用于非阻断型提示弹窗）
    mask: {
      type: Boolean,
      default: true
    },
    // 关闭时销毁子元素
    destroyOnClose: {
      type: Boolean,
      default: true
    },
    // 自定义类名
    customClass: {
      type: String,
      default: ''
    },
    // 是否全屏（移动端）
    fullscreen: {
      type: Boolean,
      default: false
    },
    // 是否始终全屏（包括PC端）
    alwaysFullscreen: {
      type: Boolean,
      default: false
    },
    // 是否上下左右居中（PC端弹窗常用）
    centered: {
      type: Boolean,
      default: true
    },
    // ========== 自定义头部配置 ==========
    // 头部标题
    headerTitle: {
      type: String,
      default: ''
    },
    // 头部图片
    headerImg: {
      type: String,
      default: ''
    },
    // 头部副标题
    headerSubtitle: {
      type: String,
      default: ''
    },
    // 头部容器样式类
    headerClass: {
      type: String,
      default: 'flex items-center justify-between gap-2 px-3 py-2.5 bg-gradient-to-br from-purple-50/40 via-indigo-50/20 to-blue-50/40 dark:from-purple-900/15 dark:via-indigo-900/8 dark:to-blue-900/15 border-b border-black/[0.04] dark:border-white/[0.06]'
    },
    // 头部标题样式类
    headerTitleClass: {
      type: String,
      default: 'text-lg font-bold text-slate-800 dark:text-slate-100'
    },
    // 头部副标题样式类
    headerSubtitleClass: {
      type: String,
      default: 'mt-0.5 font-normal text-xs text-slate-500 dark:text-slate-400'
    },
    // 是否显示头部关闭按钮
    showHeaderClose: {
      type: Boolean,
      default: true
    },
    // 头部关闭按钮样式类
    headerCloseClass: {
      type: String,
      default: 'flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-base text-slate-500 transition-colors hover:bg-purple-100 dark:text-slate-400 dark:hover:bg-purple-900/30'
    },
    /** 内容滚动区内边距与背景（弹窗场景可传紧凑类，如 px-3 py-2.5） */
    bodyClass: {
      type: String,
      default: 'px-3 py-2.5'
    },
    /** 内容区是否自行滚动；false 时由插槽内部管理滚动（如测评弹窗固定 Tab/分页） */
    bodyScrollable: {
      type: Boolean,
      default: true
    },
    // ========== 自定义底部配置 ==========
    // 底部容器样式类
    footerClass: {
      type: String,
      default: 'flex justify-center gap-2 border-t border-black/[0.04] px-4 py-3 dark:border-white/[0.06]'
    },
    // 是否显示重置按钮
    showResetButton: {
      type: Boolean,
      default: true
    },
    // 重置按钮文字
    resetButtonText: {
      type: String,
      default: '重置'
    },
    // 重置按钮样式类
    resetButtonClass: {
      type: String,
      default: 'inline-flex h-9 min-w-[96px] cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-slate-100 px-4 text-sm text-purple-600 transition-colors hover:bg-slate-200/80 dark:bg-slate-700/60 dark:text-purple-400 dark:hover:bg-slate-700'
    },
    // 是否显示确认按钮
    showConfirmButton: {
      type: Boolean,
      default: true
    },
    // 确认按钮文字
    confirmButtonText: {
      type: String,
      default: '保存'
    },
    // 确认按钮样式类
    confirmButtonClass: {
      type: String,
      default: 'inline-flex h-9 min-w-[96px] cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 px-4 text-sm text-white shadow-sm transition-colors hover:from-purple-500 hover:to-indigo-500'
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      _resizeHandler: null
    }
  },
  computed: {
    // 优先使用 open，如果没有则使用 visible
    modalOpen () {
      return this.open !== undefined ? this.open : this.visible
    },
    isMobile () {
      // 使用响应式的 windowWidth 触发重新计算
      const _ = this.windowWidth
      // 强制刷新 viewport 缓存并获取最新值
      const { refreshViewport, isMobile } = require('@/utils/viewport')
      refreshViewport()
      return isMobile()
    },
    isTablet () {
      // 使用响应式的 windowWidth 触发重新计算
      const _ = this.windowWidth
      // 强制刷新 viewport 缓存并获取最新值
      const { refreshViewport, isTablet } = require('@/utils/viewport')
      refreshViewport()
      return isTablet()
    },
    modalWidth () {
      if (this.alwaysFullscreen) {
        return '100%'
      }
      if (this.fullscreen && this.isMobile) {
        return '100%'
      }
      return this.width
    },
    wrapClassName () {
      const classes = ['custom-modal-wrapper']
      if (this.customClass) {
        classes.push(this.customClass)
      }
      if (this.centered && !this.alwaysFullscreen && !(this.fullscreen && this.isMobile)) {
        classes.push('custom-modal-centered')
      }
      if (this.alwaysFullscreen) {
        classes.push('custom-modal-always-fullscreen')
      } else if (this.fullscreen && this.isMobile) {
        classes.push('custom-modal-fullscreen')
      }
      return classes.join(' ')
    }
  },
  methods: {
    debounce(fn, delay) {
      let timer = null
      return function(...args) {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
          fn.apply(this, args)
        }, delay)
      }
    },
    updateWindowSize() {
      const oldWidth = this.windowWidth
      const oldHeight = this.windowHeight
      
      this.windowWidth = window.innerWidth
      this.windowHeight = window.innerHeight
      
      // 只有真正变化时才输出日志
      if (oldWidth !== this.windowWidth || oldHeight !== this.windowHeight) {
        // console.log('📐 [CustomModal] 窗口尺寸已更新:', {
        //   width: this.windowWidth,
        //   height: this.windowHeight,
        //   isMobile: this.isMobile,
        //   isTablet: this.isTablet
        // })
      }
    },
    handleCancel () {
      this.$emit('cancel')
      this.$emit('update:visible', false)
      this.$emit('update:open', false)
    },
    handleReset () {
      this.$emit('reset')
    },
    handleConfirm () {
      this.$emit('confirm')
    }
  },
  mounted() {
    // 使用 $nextTick 确保组件完全挂载后再调用
    this.$nextTick(() => {
      this.updateWindowSize()
      
      // 创建防抖的 resize 处理器
      this._resizeHandler = this.debounce(this.updateWindowSize, 150)
      
      window.addEventListener('resize', this._resizeHandler, { passive: true })
      // orientationchange 立即执行，不使用防抖
      window.addEventListener('orientationchange', this.updateWindowSize)
    })
  },
  beforeUnmount() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
      this._resizeHandler = null
    }
    window.removeEventListener('orientationchange', this.updateWindowSize)
  }
}
</script>

<style lang="scss">
/* wrap-class-name 挂在 .ant-modal-wrap 上；选择器需含 .ant-modal 以压过 antd hashPriority=high 的 CSS-in-JS */
.custom-modal-wrapper.ant-modal-wrap .ant-modal {
  top: 30px !important;
}

/* 上下左右居中：wrap 上用 flex 做居中（更稳定） */
.custom-modal-centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-modal-centered.ant-modal-wrap .ant-modal {
  top: 0 !important;
  margin: 0;
  padding-bottom: 0;
}

.custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-content {
  padding: 0;
  border-radius: 12px;
  overflow: hidden;
  /* iOS Safari 兼容性：使用 will-change 替代 translateZ(0) 避免层叠上下文问题 */
  position: relative;
  z-index: 1;
  will-change: transform;
}

.custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-header {
  margin-bottom: 0;
  padding: 0;
  border-bottom: none;
  background: transparent;
}

.custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.4;
}

.custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-body {
  padding: 0;
}

.custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-footer {
  margin-top: 0;
  padding: 0;
}

/* 移动端全屏样式 */
.custom-modal-fullscreen.ant-modal-wrap .ant-modal {
  max-width: 100%;
  margin: 0;
  top: 0 !important;
  padding: 0;
}

.custom-modal-fullscreen.ant-modal-wrap .ant-modal .ant-modal-content {
  padding: 0;
  border-radius: 0;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.custom-modal-fullscreen.ant-modal-wrap .ant-modal .ant-modal-body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

/* 始终全屏样式（包括PC端） */
.custom-modal-always-fullscreen.ant-modal-wrap .ant-modal {
  max-width: 100%;
  margin: 0;
  top: 0 !important;
  padding: 0;
}

.custom-modal-always-fullscreen.ant-modal-wrap .ant-modal .ant-modal-content {
  padding: 0;
  border-radius: 0;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.custom-modal-always-fullscreen.ant-modal-wrap .ant-modal .ant-modal-body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

/* 暗色模式适配 */
.dark .custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-content {
  background: #1f2937;
}

.dark .custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-header {
  background: transparent;
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.dark .custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-title {
  color: #f3f4f6;
}

.dark .custom-modal-wrapper.ant-modal-wrap .ant-modal .ant-modal-footer {
  border-top-color: rgba(255, 255, 255, 0.1);
}
</style>
